import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { 
  GitBranch, 
  Loader2,
  Maximize2,
  FileText,
  Package,
  FolderKanban,
  ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GraphWidget = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentLinks, setRecentLinks] = useState([]);

  const fetchGraphData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/graph/relationships`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.stats);
      
      // Get some sample links to show
      const links = response.data.links.slice(0, 4).map(link => {
        const sourceNode = response.data.nodes.find(n => n.id === (link.source?.id || link.source));
        const targetNode = response.data.nodes.find(n => n.id === (link.target?.id || link.target));
        return { ...link, sourceNode, targetNode };
      });
      setRecentLinks(links);
    } catch (error) {
      console.error('Failed to load graph data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchGraphData();
  }, [fetchGraphData]);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'project': return <FolderKanban className="w-3 h-3 text-purple-500" />;
      case 'asset': return <Package className="w-3 h-3 text-orange-500" />;
      case 'document': return <FileText className="w-3 h-3 text-blue-500" />;
      default: return <GitBranch className="w-3 h-3 text-gray-500" />;
    }
  };

  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base font-medium flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-primary" />
          Relationships
        </CardTitle>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/graph')}
          className="text-muted-foreground hover:text-foreground"
        >
          <Maximize2 className="w-4 h-4 mr-1" />
          Expand
        </Button>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Mini stats */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              <div className="text-center p-2 bg-muted/30 rounded">
                <div className="flex items-center justify-center gap-1">
                  <FolderKanban className="w-3 h-3 text-purple-500" />
                  <span className="font-semibold text-sm">{stats?.projects || 0}</span>
                </div>
                <p className="text-xs text-muted-foreground">Projects</p>
              </div>
              <div className="text-center p-2 bg-muted/30 rounded">
                <div className="flex items-center justify-center gap-1">
                  <Package className="w-3 h-3 text-orange-500" />
                  <span className="font-semibold text-sm">{stats?.assets || 0}</span>
                </div>
                <p className="text-xs text-muted-foreground">Assets</p>
              </div>
              <div className="text-center p-2 bg-muted/30 rounded">
                <div className="flex items-center justify-center gap-1">
                  <FileText className="w-3 h-3 text-blue-500" />
                  <span className="font-semibold text-sm">{stats?.documents || 0}</span>
                </div>
                <p className="text-xs text-muted-foreground">Docs</p>
              </div>
              <div className="text-center p-2 bg-muted/30 rounded">
                <div className="flex items-center justify-center gap-1">
                  <GitBranch className="w-3 h-3 text-green-500" />
                  <span className="font-semibold text-sm">{stats?.total_links || 0}</span>
                </div>
                <p className="text-xs text-muted-foreground">Links</p>
              </div>
            </div>

            {/* Sample connections */}
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium">Recent Connections</p>
              {recentLinks.length > 0 ? (
                recentLinks.map((link, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs p-2 bg-muted/20 rounded">
                    {link.sourceNode && getTypeIcon(link.sourceNode.type)}
                    <span className="truncate flex-1">{link.sourceNode?.name || 'Unknown'}</span>
                    <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                    {link.targetNode && getTypeIcon(link.targetNode.type)}
                    <span className="truncate flex-1">{link.targetNode?.name || 'Unknown'}</span>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-muted-foreground text-xs">
                  No connections yet
                </div>
              )}
            </div>

            {/* View all button */}
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full mt-3"
              onClick={() => navigate('/graph')}
            >
              View All Relationships
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default GraphWidget;
